import { TopNavigation, TopNavigationItems } from "@fidelix/fx-miranda";
import { t } from "@lingui/core/macro";
import { usePathNavigator, useNavigatonPath } from "~utils/url";
import { useParams } from "react-router-dom";
import { CompanySelect } from "../company/company-select";
import { AlarmNotificationBell } from "../alarm/alarm-notification-bell";
import { NavMenu } from "./nav-menu";
import { Permissions } from "../const";
import { HasPermission, hasPermission } from "../utils/permission-helper";
import { useUserContext } from "../user/user-context";
import { DeviceNavBarIcon } from "../device/device-navbar-icon";

/**
 * Find the active route by searching which of the inputted routenames occur last in the location path
 * We have routes that have multiple occurences of our routenames for example:
 *
 * company/5/devices/4/points/588870
 * (this has both /devices and /points in it)
 *
 * And this was causing problems in the top navigation bar context. It would show Devices
 * selected eventhough we were clearly in Points
 */

export const mainRoutes = [
  "overview",
  "graphics",
  "alarms",
  "schedule",
  "history",
  "energy",
  "points",
  "logs",
  "devices",
  "menus",
] as const;

export type MainRoute = (typeof mainRoutes)[number];

export function getActiveRoute(routes: readonly string[] = mainRoutes): string {
  return routes.reduce<{
    routeName: string;
    routeIndex: number;
  }>(
    (a, c) => {
      const idx = location.pathname.lastIndexOf(c);
      if (idx > a.routeIndex) {
        return { routeName: c, routeIndex: idx };
      }
      return a;
    },
    { routeName: "", routeIndex: 0 },
  ).routeName;
}

export function Navbar() {
  const { to } = useNavigatonPath();
  const { companyId } = useParams();
  const { withSubUrl } = usePathNavigator();
  const { user } = useUserContext();
  const activeMainRoute = getActiveRoute(mainRoutes);
  const activeSubRoute = getActiveRoute([
    "logs/event",
    "logs/alarm",
    "logs/connection",
    "schedule/calendars",
    "schedule/timeschedules",
    "history",
    "energy",
  ]);

  const permissions = user?.permissions || [];

  const historyItems = [
    {
      to: to("history"),
      label: t`History`,
      isActive: activeSubRoute === "history",
      isVisible: hasPermission(Permissions.HISTORY_VIEW, permissions),
    },
    {
      to: to("energy"),
      label: t`Energy reports`,
      isActive: activeSubRoute === "energy",
      isVisible: hasPermission(Permissions.ENERGYREPORTS_VIEW, permissions),
    },
  ].filter((o) => o.isVisible);

  const items: TopNavigationItems = [
    {
      to: to("overview"),
      label: t`Overview`,
      isActive: activeMainRoute === "overview",
      isVisible: hasPermission(Permissions.ANALYTICS, permissions),
    },
    {
      to: to("graphics"),
      label: t`Graphics`,
      isActive: activeMainRoute === "graphics" || activeMainRoute === "menus",
      isVisible: hasPermission(Permissions.GRAPHIC_VIEW, permissions),
    },
    {
      to: to("alarms"),
      label: t`Alarms`,
      isActive: activeMainRoute === "alarms",
      isVisible: hasPermission(Permissions.ALARMS_VIEW, permissions),
    },
    {
      to: to("schedule"),
      label: t`Schedules`,
      isActive: activeMainRoute === "schedule",
      isVisible:
        hasPermission(Permissions.CALENDARS_VIEW, permissions) ||
        hasPermission(Permissions.POINTS_VIEW, permissions),
      items: [
        {
          to: to("schedule/calendars"),
          label: t`Calendars`,
          isActive: activeSubRoute === "schedule/calendars",
          isVisible: hasPermission(Permissions.CALENDARS_VIEW, permissions),
        },
        {
          to: to("schedule/timeschedules"),
          label: t`Time schedules`,
          isActive: activeSubRoute === "schedule/timeschedules",
          isVisible: hasPermission(Permissions.POINTS_VIEW, permissions),
        },
      ].filter((o) => o.isVisible),
    },

    ...(historyItems.length > 1
      ? [
          {
            to: to("history"),
            label: t`History`,
            isActive: activeMainRoute === "history",
            isVisible:
              hasPermission(Permissions.HISTORY_VIEW, permissions) ||
              hasPermission(Permissions.ENERGYREPORTS_VIEW, permissions),
            items: historyItems,
          },
        ]
      : historyItems),
    {
      to: to("points"),
      label: t`Points`,
      isActive: activeMainRoute === "points",
      isVisible: hasPermission(Permissions.POINTS_VIEW, permissions),
    },
    {
      to: to("logs"),
      label: t`Logs`,
      isActive: activeMainRoute === "logs",
      isVisible:
        hasPermission(Permissions.EVENTLOG_VIEW, permissions) ||
        hasPermission(Permissions.ALARMLOG_VIEW, permissions),
      items: [
        {
          to: to("logs/event"),
          label: t`Event log`,
          isActive: activeSubRoute === "logs/event",
          isVisible: hasPermission(Permissions.EVENTLOG_VIEW, permissions),
        },
        {
          to: to("logs/alarm"),
          label: t`Alarm log`,
          isActive: activeSubRoute === "logs/alarm",
          isVisible: hasPermission(Permissions.ALARMLOG_VIEW, permissions),
        },
        {
          to: to("logs/connection"),
          label: t`Connection log`,
          isActive: activeSubRoute === "logs/connection",
          isVisible: hasPermission(Permissions.CONNECTIONLOG_VIEW, permissions),
        },
      ].filter((o) => o.isVisible),
    },
    {
      to: to("devices"),
      label: t`Devices`,
      isActive: activeMainRoute === "devices",
      isVisible: hasPermission(Permissions.DEVICES_VIEW, permissions),
    },
  ].filter((o) => o.isVisible);

  const menuItems: {
    to: string;
    label: string;
    desc: string;
    permission?: Permissions;
  }[] = [
    {
      to: withSubUrl(`https://analytics.fidelix.com/company/${companyId}`),
      label: t`Fidelix Analytics`,
      desc: t`Monitor and control building operations`,
      permission: Permissions.ANALYTICS,
      active: true,
    },
    {
      to: withSubUrl(`https://assets.fidelix.com/company/${companyId}`),
      label: t`Fidelix Assets`,
      desc: t`Connect devices to the cloud`,
      permission: Permissions.ASSETS,
    },
    {
      to: withSubUrl(`https://admin.fidelix.com/${companyId}/users`),
      label: t`Fidelix Admin`,
      desc: t`Manage customers and user rights`,
      permission: Permissions.COMPANY_ADMIN,
    },
  ].filter((item) => hasPermission(item.permission, permissions));

  return (
    <TopNavigation variant="primary">
      <TopNavigation.Logo text="Analytics" menuItems={menuItems} />
      <TopNavigation.Items items={items} />
      <TopNavigation.Actions>
        <CompanySelect />
        <HasPermission permission={Permissions.DEVICES_VIEW}>
          <DeviceNavBarIcon />
        </HasPermission>
        <HasPermission permission={Permissions.ALARMS_VIEW}>
          <AlarmNotificationBell />
        </HasPermission>
        <NavMenu />
      </TopNavigation.Actions>
    </TopNavigation>
  );
}
